
import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = "", delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('active');
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [delay]);

  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
};

export const SectionHero: React.FC<{ title: string; image: string; subtitle?: string; height?: string }> = ({ title, image, subtitle, height = "h-[50vh]" }) => (
  <div className={`relative ${height} w-full overflow-hidden flex items-center justify-center bg-concrete-dark`}>
    <div className="absolute inset-0 z-10 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] opacity-20"></div>
    <div className="absolute inset-0 z-0">
      <img src={image} alt={title} className="w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-t from-concrete-dark via-concrete-dark/60 to-transparent"></div>
    </div>
    <div className="relative z-20 container mx-auto px-6 mt-16">
      <Reveal>
        <div className="border-l-4 border-brand-red pl-6 md:pl-10">
            {subtitle && <p className="text-brand-red font-mono font-bold tracking-widest uppercase text-xs md:text-sm mb-3 animate-fade-in-up flex items-center gap-2"><span className="w-8 h-[1px] bg-brand-red"></span> {subtitle}</p>}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white uppercase leading-none shadow-black drop-shadow-xl max-w-5xl">
            {title}
            </h1>
        </div>
      </Reveal>
    </div>
  </div>
);

export const TechCard: React.FC<{ title: string; children: React.ReactNode; icon?: React.ReactNode; number?: string }> = ({ title, children, icon, number }) => (
  <div className="group bg-white p-8 border border-slate-200 hover:border-brand-red transition-all duration-300 relative overflow-hidden shadow-sm hover:shadow-xl">
    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-slate-300 group-hover:border-brand-red transition-colors"></div>
    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-slate-300 group-hover:border-brand-red transition-colors"></div>
    <div className="relative z-10">
      <div className="flex justify-between items-start mb-6">
         <div className="p-3 bg-slate-50 text-brand-red border border-slate-100 group-hover:bg-brand-red group-hover:text-white transition-colors duration-300 rounded-none">
           {icon}
         </div>
         {number && <span className="font-mono text-4xl font-bold text-slate-100 group-hover:text-slate-200 transition-colors">{number}</span>}
      </div>
      <h3 className="text-xl font-display font-bold text-slate-900 mb-3 uppercase tracking-wide group-hover:text-brand-red transition-colors">{title}</h3>
      <div className="text-slate-600 leading-relaxed text-sm border-t border-slate-100 pt-4 mt-2">
        {children}
      </div>
    </div>
  </div>
);

export const StickyCTA: React.FC = () => (
  <div className="fixed bottom-0 w-full bg-white border-t-4 border-brand-red py-3 px-6 z-40 md:hidden flex justify-between items-center shadow-[0_-4px_15px_-5px_rgba(0,0,0,0.2)]">
    <div>
      <p className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest">Support Technique</p>
      <p className="font-display font-bold text-slate-900 text-lg">450 372-2565</p>
    </div>
    <Link to="/contact" className="bg-brand-red text-white px-5 py-2 font-display font-bold text-sm shadow-md hover:bg-brand-red-dark transition-colors uppercase tracking-wider">
      Soumission
    </Link>
  </div>
);
